import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {styled, Typography, useMediaQuery, useTheme} from '@mui/material';
import {StripeForm} from '@zoom/billing-stripe-react';
import {IconChevronLeftSmall, IconCreditCard, IconInformation} from '@zoom/icons-react';
import {Banner, Box, Button, Dialog, Skeleton, Spinning, Stack, Toast, Tooltip} from '@zoom/react-ui';
import {addMinutes, format} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {v4 as uuidv4} from 'uuid';

import BookingFormContext from './BookingFormContext';
import PayLoadingDialog from './PayLoadingDialog';

import {VIEW_STATE} from 'Components/Appointments';
import ApptsContext from 'Components/Appointments/ApptsContext';
import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import EventInfo from 'Components/EventInfo';
import {CaptchaError} from 'Error/CaptchaError';
import {HttpError} from 'Error/HttpError';
import {
  calculateTaxesThunk,
  capturePaymentWithPollingThunk,
  createOrderThunk,
} from 'Store/BookAppointmentsStore';
import {NWS_ERROR_CODE} from 'Store/IntegrationStore';
import {fetchStripePublicKey} from 'Store/OrganizationSettingStore';
import {formatPriceTo2Decimal} from 'Utils';
import {CURRENCY_SYMBOL} from 'Utils/consts';

import '@zoom/billing-stripe-react/dist/billing-stripe-react.css';


const Title = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: 600,
  lineHeight: '1.75rem',
});

const TimeLeft = styled(Typography)(({theme}) => ({
  color: theme.palette.text.neutral,
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '1.125rem',
}));

const PaymentDetailContainer = styled(Box)(({theme}) => ({
  boxShadow: '0px 2px 4px 0px #00000014',
  border: `1px solid ${theme.palette.common.swatch[30]}`,
  borderRadius: '0.5rem',
  padding: '1rem',
}));

const FeeTextSx = (theme) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.neutral,
  lineHeight: '1.125rem',
});

const TotalTextSx = (theme) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.common.black,
});

const AutoRefundSx = (theme) => ({
  padding: '1rem',
  fontWeight: 400,
  fontSize: '0.875rem',
  color: theme.palette.text.neutral,
  lineHeight: '1.125rem',
  display: 'flex',
  flexDirection: 'column',
});

const AutoRefundBannerSx = {
  width: '22.5rem',
  marginTop: '1rem',
};

const formatTime = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const ToastTopEnd = Toast.create({placement: 'top-end', duration: 10000});

const PaymentForm = (props) => {
  const {i18n, t} = useTranslation();
  const dispatch = useDispatch();

  const {languages} = i18n;

  const {appointment, calendarId, formValues, onBook, onValidateBookSlot, startDateTime} = props;

  const price = appointment.scheduleCost;
  const currencyType = appointment.currency;
  const {
    bizOrderId,
    setBizOrderId,
    setCurrView,
  } = useContext(BookingFormContext);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const creatorDisplayName = useSelector((s) => s.bookAppointments.createrDisplayName);
  const stripePublicKey = useSelector((s) => s.organizationSettingState.stripePublicKey);
  const autoRefundPolicy = useSelector((s) => s.bookAppointments.autoRefundPolicy);


  const {
    isBookerVoting,
    timeZone,
    votes,
  } = useContext(ApptsContext);

  const [timeLeft, setTimeLeft] = useState(15 * 60);

  const [taxes, setTaxes] = useState(null);

  const [totalAmount, setTotalAmount] = useState(price);
  const [showLoading, setShowLoading] = useState(false);

  const [taxStatus, setTaxState] = useState(false);
  const [taxCalculating, setTaxCalculating] = useState(false);
  const [hasExtraTax, setHasExtraTax] = useState(true);

  const [onlineTaxResult, setOnlineTaxResult] = useState(true);

  const [clientSecret, setClientSecret] = useState('');
  const [thirdAccountId, setThirdAccountId] = useState('');

  const calculateTaxUuid = useRef();

  const displayPrice = `${CURRENCY_SYMBOL[currencyType]}${formatPriceTo2Decimal(price)}`;
  const displayTotalAmount = `${CURRENCY_SYMBOL[currencyType]}${formatPriceTo2Decimal(totalAmount)}`;
  const displayTaxes = taxes !== null && taxStatus ?
    `${CURRENCY_SYMBOL[currencyType]}${formatPriceTo2Decimal(taxes)}` :
    t('payment.enterBillingAddress');

  const userInitInfo = useMemo(() => ({
    firstName: formValues?.bookerFirstName,
    lastName: formValues?.bookerLastName,
    email: formValues?.bookerEmail,
  }), [formValues]);

  const limitTime = useMemo(() => autoRefundPolicy > 0 ?
    toDateTime(addMinutes(startDateTime || 0, -autoRefundPolicy * 60), timeZone).valueOf() :
    0,
  [autoRefundPolicy, startDateTime, timeZone]);

  const isAutoRefundLimit = autoRefundPolicy !== 0 && (autoRefundPolicy === -1 || Date.now() > limitTime);

  const autoRefundTips = useMemo(() => {
    if (autoRefundPolicy === 0) {
      return t('payment.freeCancellationOrReschedulingAtAnyTime');
    }
    if (autoRefundPolicy === -1) {
      return t('payment.noAutoRefund');
    }
    if (isAutoRefundLimit) {
      return t('payment.autoRefundLimit');
    }
    if (autoRefundPolicy > 0) {
      const timeFormatted = format(limitTime, 'ccc, MMM d, h:mm a');
      return t('payment.autoRefundBefore', [timeFormatted]);
    }
  }, [autoRefundPolicy, isAutoRefundLimit, limitTime, t]);

  useEffect(() => {
    if (!stripePublicKey) {
      dispatch(fetchStripePublicKey());
    }
  }, [dispatch, stripePublicKey]);

  useEffect(() => {
    setTotalAmount(price);
  }, [price]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      const newTimeLeft = timeLeft - 1;
      setTimeLeft(newTimeLeft);
      if (newTimeLeft <= 0) {
        console.log('time left is 0, reload the page');
        clearInterval(timer);
        // the page will go to date view automatically after reload
        Dialog.confirm({
          title: t('payment.paymentTimeout'),
          children: t('payment.paymentTimeoutDesc'),
          okText: t('payment.returnToBookingPage'),
          backdropClickClosable: false,
          escapeKeyDownClosable: false,
          onOk: () => {
            window.location.reload();
          },
          cancelText: null,
        });
        return;
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, t]);

  const handleOnTaxesCalculate = useCallback(async (info) => {
    try {
      setTaxState(false);
      setTaxCalculating(true);
      setOnlineTaxResult(true);

      const uuid = uuidv4();
      calculateTaxUuid.current = uuid;

      const params = {
        currency: currencyType,
        orderItemDTOList: [{
          productId: appointment.id,
          itemName: appointment.summary,
          itemId: `${appointment.id}-1`,
          quantity: 1,
          price: String(appointment.scheduleCost),
          eventOnOfflineType: 0,
          ticketOnOfflineType: 0,
        }],
        payerAddress: {
          // firstName: info.firstName,
          // lastName: info.lastName,
          name: `${info.firstName} ${info.lastName}`,
          email: info.email,
          companyName: info.company,
          country: info.country.value,
          city: info.city,
          address1: info.street,
          state: info.state.value,
          zipcode: info.zip,
          streetAddress: info.street,
          suite: info.apt,
          taxId: info.vat,
          uin: info.uin,
        },
      };
      const res = await dispatch(calculateTaxesThunk({params, user: calendarId})).unwrap();

      if (uuid !== calculateTaxUuid.current) {
        return;
      }

      setTaxes(res.exclusiveTax);
      setTotalAmount(res.amount);
      setTaxState(true);
      setHasExtraTax(res.exclusiveTax > 0);
      if (!res.attendeeTinValid) {
        setOnlineTaxResult(false);
        throw new Error('Attendee or host TIN is invalid');
      }
      return res;
    } catch (error) {
      ToastTopEnd.error({
        title: t('payment.unexpectedError'),
        content: t('payment.unexpectedErrorTips'),
        closable: true,
      });
      console.error('calculate taxes error: ', error);
      throw error;
    } finally {
      setTaxCalculating(false);
    }
  }, [dispatch, appointment, calendarId, currencyType, t]);

  const handleOnCreateOrder = useCallback(async (values, taxResult) => {
    if (!values) return;

    setClientSecret('');
    setBizOrderId('');
    setThirdAccountId('');

    try {
      const params = {
        payChannel: 2,
        payScene: 1,
        currency: currencyType,
        appointmentId: appointment.id,
        // payerName: `${firstName} ${lastName}`,
        // payerEmail: email,
        totalFee: String(taxResult.amount),
        totalTax: String(taxResult.exclusiveTax),
        orderItemDTOList: [
          {
            productId: appointment.id,
            itemName: appointment.summary,
            itemId: `${appointment.id}-1`,
            quantity: 1,
            price: String(price),
            eventOnOfflineType: 0,
            ticketOnOfflineType: 0,
          },
        ],
        payerAddress: {
          // firstName: values.firstName,
          // lastName: values.lastName,
          name: `${values.firstName} ${values.lastName}`,
          email: values.email,
          companyName: values.company,
          country: values.country.value,
          city: values.city,
          state: values.state.value,
          zipcode: values.zip,
          streetAddress: values.street,
          suite: values.apt,
          taxId: values.vat,
          uin: values.uin,
        },
      };

      const res = await dispatch(createOrderThunk({params, user: calendarId})).unwrap();
      setClientSecret(res.approveUrl);
      setBizOrderId(res.bizOrderId);
      setThirdAccountId(res.thirdAccountId);
    } catch (error) {
      console.error('create order error: ', error);
      ToastTopEnd.error({
        title: t('payment.createOrderError'),
        content: t('common.pleaseTryAgainLater'),
        closable: true,
      });
      throw error;
    }
  }, [
    dispatch, currencyType, price, appointment,
    calendarId, t, setBizOrderId,
  ]);

  const handleOnContinueToPayment = useCallback(async (values) => {
    if (!values) return;
    const result = await handleOnTaxesCalculate(values);
    if (result) {
      await handleOnCreateOrder(values, result);
    }
  }, [handleOnTaxesCalculate, handleOnCreateOrder]);

  const showSlotNotAvailable = useCallback(() => {
    Dialog.confirm({
      title: t('payment.slotNotAvailable'),
      children: t('payment.slotNotAvailableDesc'),
      backdropClickClosable: false,
      escapeKeyDownClosable: false,
      onOk: () => {
        window.location.reload();
      },
      okText: t('payment.selectAnotherTime'),
      cancelText: null,
    });
  }, [t]);

  const handleOnPayAndBook = useCallback(async (paymentResult) => {
    if (!paymentResult.success) {
      const errorMsg = paymentResult.error?.message ||
        paymentResult.error?.originalMessage ||
        t('common.pleaseTryAgainLater');
      ToastTopEnd.error({
        title: t('payment.paymentFailed'),
        content: errorMsg,
        closable: true,
      });
      setShowLoading(false);
      return;
    }
    try {
      // Use polling version for better reliability - polls every 10 seconds, max 6 attempts
      const captureResult = await dispatch(capturePaymentWithPollingThunk({
        bizOrderId,
        user: calendarId,
        maxAttempts: 6,
        intervalMs: 10000,
      })).unwrap();
      if (!captureResult) {
        return;
      }

      const bookResult = await onBook(formValues, {bizOrderId, skipPayment: true});
      if (bookResult) {
        setCurrView(VIEW_STATE.CONFIRM_VIEW);
      }
    } catch (error) {
      console.error('pay and book error: ', error);
    } finally {
      setShowLoading(false);
    }
  }, [dispatch, bizOrderId, calendarId, setCurrView, onBook, formValues, t]);

  const handleOnBack = useCallback(() => {
    setCurrView(VIEW_STATE.FORM_VIEW);
  }, [setCurrView]);

  const handleOnBeforePayment = useCallback(async () => {
    setShowLoading(true);
    try {
      const passcode = !appointment.verify ? '' : window.sessionStorage.getItem('code').toString();
      await onValidateBookSlot(calendarId, appointment.id, formValues, passcode, bizOrderId);
    } catch (e) {
      console.error('Failed to book:', e, e.errorCode, e.message);
      let reason = '';
      if (e instanceof CaptchaError) {
        reason = t('booking.captchaPrompt');
      } else if (e instanceof HttpError) {
        const errMsg = (e.body?.error?.message) || '';
        // show the slot not available dialog if the time slot is busy and only show select slot
        if (e.errorCode === 400 && errMsg.includes('Requested time slot busy')) {
          showSlotNotAvailable();
          setShowLoading(false);
          return false;
        }
        // show the booking page is inactive or deleted dialog if the booking page is inactive or deleted
        if (e.errorCode === 400 &&
          (errMsg.includes('Booking page is inactive') ||
          errMsg.includes('Booking page has been deleted') ||
          errMsg.includes('Not found'))) {
          // reload the page to show the booking page is inactive or deleted
          setCurrView(VIEW_STATE.PAYMENT_BOOKING_PAGE_EXPIRED);
          return false;
        }
        switch (e.errorCode) {
          case 400:
            if (errMsg.includes('verification code')) {
              reason = t('booking.incorrectPasscode');
            } else {
              reason = errMsg || t('booking.genericHttpValidationError');
            }
            break;
          case 401:
            reason = errMsg || 'Unauthorized';
            break;
          case 403:
            reason = t('booking.incorrectPasscode');
            window.sessionStorage.removeItem('code');
            break;
          case 404:
            reason = t('error.errorMsgBody404');
            break;
          case 500:
            reason = t('error.errorMsgBody500');
            if ([NWS_ERROR_CODE.INVALID_GRANT,
              'Invalid Credentials', 'connection is empty'].some((item) => e.message?.includes(item))) {
              reason = t('booking.hostAccountInvalid');
            }
            if (errMsg.includes('Permission deny')) {
              reason = t('booking.hostAccountInvalid');
            }
            // failed to create Meeting link, show the reason let user know what happened
            if (errMsg && (
              errMsg.startsWith('ZoomMeetingError') ||
              errMsg.startsWith('GoogleMeetError') ||
              errMsg.startsWith('TeamsMeetingError')
            )) {
              reason = errMsg;
            }
            break;
          default:
            reason = t('booking.genericHttpError');
            break;
        }
      } else {
        reason = t('booking.genericError');
      }
      setShowLoading(false);
      ToastTopEnd.error({
        placement: 'top-end',
        title: t('payment.bookingError'),
        content: reason,
        closable: true,
      });
      return false;
    }

    return true;
  }, [onValidateBookSlot, appointment, calendarId, formValues, bizOrderId, setCurrView, showSlotNotAvailable, t]);

  const handleOnAddressEdit = useCallback(() => {
    setClientSecret('');
  }, []);

  const btnInformation = useMemo(() => (
    <Tooltip
      title={t('payment.bookAndPayTips')}
      arrow
      sx={{width: '22.5rem'}}
    >
      <IconInformation />
    </Tooltip>
  ), [t]);

  return (
    <Stack direction={isSmallScreen ? 'column' : 'row'} gap="3rem" justifyContent="center">
      <Box>
        <Button type="tertiary" onClick={handleOnBack}>
          <IconChevronLeftSmall />
          {t('common.back')}
        </Button>
      </Box>
      <Box width={isSmallScreen ? '100%' : '30rem'}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb="1rem">
          <Title>{t('payment.payAndBook')}</Title>
          <TimeLeft>
            {t('payment.timeLeft')}: {formatTime(timeLeft)}
          </TimeLeft>
        </Stack>
        <Stack gap="0.5rem">
          <StripeForm
            clientSecret={clientSecret}
            stripePublicKey={stripePublicKey}
            locale={languages?.[1] || 'en'}
            account={thirdAccountId}
            taxStatus={true}
            userInitInfo={userInitInfo}
            onlineTaxResult={onlineTaxResult}
            onFormReady={handleOnContinueToPayment}
            onCanContinuePay={handleOnBeforePayment}
            onPayResult={handleOnPayAndBook}
            payAndBookBtnProps={{
              endIcon: btnInformation,
            }}
            continueToPaymentBtnText={taxCalculating ? t('payment.calculatingTax') : ''}
            continueToPaymentBtnProps={taxCalculating ? {
              endIcon: <Spinning size="small" />,
              disabled: true,
              loading: false,
            } : {}}
            onEditClick={handleOnAddressEdit}
          />
        </Stack>
      </Box>
      <Box>
        <PaymentDetailContainer width={isSmallScreen ? '100%' : '22.5rem'}>
          <EventInfo
            appointment={appointment}
            displayName={creatorDisplayName}
            showAvatar
            startDateTime={startDateTime}
            timeZone={timeZone}
            isBookerVoting={isBookerVoting}
            votes={votes}
            hiddenFee={true}
          />
          <Stack gap="0.5rem">
            {(hasExtraTax || taxCalculating) && (<>
              <Stack direction="row" height="1.125rem">
                <Stack direction="row" gap="0.5rem" width="6rem">
                  <IconCreditCard style={{position: 'relative', top: '-1px'}} />
                  <Typography sx={FeeTextSx}>{t('payment.fee')}</Typography>
                </Stack>
                <Typography sx={FeeTextSx}>{displayPrice}</Typography>
              </Stack>
              <Stack direction="row" height="1.125rem">
                <Stack direction="row" gap="0.5rem" width="6rem">
                  <Box width="1rem" />
                  <Typography sx={FeeTextSx}>{t('payment.taxes')}</Typography>
                </Stack>
                <Typography sx={FeeTextSx}>
                  {taxCalculating ? <Skeleton variant="text" width="3rem" /> : displayTaxes}
                </Typography>
              </Stack>
            </>)}
            <Stack direction="row">
              <Stack direction="row" gap="0.5rem" width="6rem">
                {!hasExtraTax ?
                  <IconCreditCard style={{position: 'relative', top: '-1px'}} /> :
                  <Box width="1rem" />
                }
                <Typography sx={TotalTextSx}>{t('payment.total')}</Typography>
              </Stack>
              <Typography sx={TotalTextSx}>
                {taxCalculating ? <Skeleton variant="text" width="5rem" /> : displayTotalAmount}
              </Typography>
            </Stack>
          </Stack>
        </PaymentDetailContainer>
        {!isAutoRefundLimit? (
          <Box sx={AutoRefundSx} width={isSmallScreen ? '100%' : '22.5rem'}>
            <span style={{fontWeight: 600}}>{t('payment.refundPolicy')}: </span>
            <span>{autoRefundTips}</span>
          </Box>
        ) : (
          <Banner
            width={isSmallScreen ? '100%' : '22.5rem'}
            sx={AutoRefundBannerSx}
            type="warn"
            content={autoRefundTips}
            closable={false}
          />
        )}
      </Box>
      <PayLoadingDialog
        open={showLoading}
        content={t('payment.processingPayment')}
        description={t('payment.processingPaymentDesc')}
      />
    </Stack>
  );
};

export default PaymentForm;
